import { useTheme } from '@mui/material';
import { Chart as ChartJS, ChartOptions } from 'chart.js';
import _ from 'lodash';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { FeatureEnum } from '../../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { useBlueberryDistribution } from '../../scanDataApi/hooks/useBlueberryDistribution';
import { useBlueberryFlowerFeatures } from '../../scanDataApi/hooks/useBlueberryFlowerFeatures';
import { selectCurrBlueberryClass } from '../../select/selectSlice';
import { useChartFeatureMeasureLabels } from './useChartLabels';
export const useBlueberryDistributionChart = (
  _chart?: ChartJS<'bar', number[], unknown> | null,
  plugins?: ChartOptions['plugins']
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  // Get the x and y axis labels for the current chart feature and dimension
  const labelResult = useChartFeatureMeasureLabels();
  const { data: datasets, isLoading } = useBlueberryDistribution();

  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
  const flowerFeatures = useBlueberryFlowerFeatures();

  const [flowerData, blueberryData] = useMemo(() => {
    if (flowerFeatures.length === 0) {
      return [[], datasets];
    }
    return _.partition(datasets, (dataset) => {
      return (
        dataset.label && flowerFeatures.includes(dataset.label as FeatureEnum)
      );
    });
  }, [datasets, flowerFeatures]);

  const data = useMemo(() => {
    let displayData = datasets;
    if (currBlueberryClass) {
      if (isBlueberryFeature(currBlueberryClass)) {
        displayData = blueberryData;
      } else if (isBlueberryFlowerFeature(currBlueberryClass)) {
        displayData = flowerData;
      }
    }
    return {
      datasets:
        displayData?.map((el) => ({ ...el, label: t(el.label!) })) || [],
      labels: [t('Blueberry Distribution')],
      rawData: displayData,
    };
  }, [datasets, t, currBlueberryClass, blueberryData, flowerData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
      ...plugins,
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        ticks: {
          color: theme.colors.common.white,
        },
        grid: {
          display: true,
          color: theme.colors.darkTheme.grey.muted,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.yLabel : '',
          color: theme.colors.common.white,
        },
      },
      x: {
        ticks: {
          display: false, // for color distribution chart, we don't need x axis ticks
          color: theme.colors.common.white,
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: labelResult ? labelResult.xLabel : '',
          color: theme.colors.common.white,
          padding: {
            // for color distribution chart, since we don't have x axis ticks or values, we can remove the title padding so it's closer to the chart
            top: 3,
            bottom: 0,
            left: 0,
            right: 0,
          },
        },
      },
    },
  };

  const result = useMemo(
    () => ({
      data,
      options,
      isLoading,
    }),
    [data, options]
  );

  return result;
};
